import React, {useMemo} from "react";
import {ChevronRightIcon} from "@heroicons/react/24/outline";
import {useMobileViewport} from "../../hooks/useMobileViewport";
import {
    buildLinkedGridColumnLayout,
    buildLinkedGridTemplate,
    formatLinkedCellText,
    isDetailColumn,
    renderLinkedGridCell,
    resolvePrimaryColumnKey,
} from "./detailLinkedListCell";

function stopRowClickTarget(e) {
    return e.target.closest(
        "button, a, input, textarea, select, [role='combobox'], [data-stop-row-click]"
    );
}

function LinkedListMobileRow({row, columns, primaryKey, interactive, onRowClick, rowIndex}) {
    const primaryCol = columns.find((col) => col.key === primaryKey);
    const title = primaryCol ? formatLinkedCellText(row, primaryCol) : "—";
    const metaCols = columns.filter((col) => col.key !== primaryKey && !isDetailColumn(col));
    const detailCols = columns.filter((col) => isDetailColumn(col));

    const rowProps = interactive
        ? {
              className: "asset-linked-list-item is-clickable",
              onClick: (e) => {
                  if (stopRowClickTarget(e)) return;
                  onRowClick(row);
              },
              onKeyDown: (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onRowClick(row);
                  }
              },
              tabIndex: 0,
              role: "button",
          }
        : {className: "asset-linked-list-item"};

    return (
        <div key={row.id ?? rowIndex} {...rowProps}>
            <div className="asset-linked-list-content">
                <div className="asset-linked-list-title">{title || "—"}</div>
                {metaCols.length > 0 ? (
                    <dl className="asset-linked-list-meta">
                        {metaCols.map((col) => {
                            const value = formatLinkedCellText(row, col);
                            if (!value) return null;
                            return (
                                <div key={col.key} className="asset-linked-list-meta-item">
                                    <dt>{col.label}</dt>
                                    <dd>{value}</dd>
                                </div>
                            );
                        })}
                    </dl>
                ) : null}
                {detailCols.map((col) => {
                    const value = formatLinkedCellText(row, col);
                    if (!value) return null;
                    return (
                        <div key={col.key} className="asset-linked-list-detail">
                            <span className="asset-linked-list-detail-label">{col.label}</span>
                            <div className="asset-linked-list-detail-body">{value}</div>
                        </div>
                    );
                })}
            </div>
            {interactive ? (
                <ChevronRightIcon className="asset-linked-list-chevron" aria-hidden />
            ) : null}
        </div>
    );
}

export default function DetailLinkedDataGrid({rows, columns, interactive, onRowClick}) {
    const isMobileViewport = useMobileViewport();
    const gridColumns = useMemo(() => buildLinkedGridColumnLayout(columns), [columns]);
    const primaryKey = useMemo(() => resolvePrimaryColumnKey(gridColumns), [gridColumns]);
    const gridTemplate = useMemo(
        () => buildLinkedGridTemplate(gridColumns, {withAction: interactive}),
        [gridColumns, interactive]
    );
    const minWidth = useMemo(
        () => gridColumns.reduce((sum, col) => sum + col.minWidth, 0) + (interactive ? 28 : 0),
        [gridColumns, interactive]
    );

    if (isMobileViewport) {
        return (
            <div className="asset-linked-list-mobile">
                {rows.map((row, rowIndex) => (
                    <LinkedListMobileRow
                        key={row.id ?? rowIndex}
                        row={row}
                        columns={gridColumns}
                        primaryKey={primaryKey}
                        interactive={interactive}
                        onRowClick={onRowClick}
                        rowIndex={rowIndex}
                    />
                ))}
            </div>
        );
    }

    return (
        <div className="detail-linked-data-grid-wrap">
            <div className="detail-linked-data-grid-inner" style={{minWidth}}>
                <div
                    className="detail-linked-data-grid-head"
                    style={{gridTemplateColumns: gridTemplate}}
                    role="row"
                >
                    {gridColumns.map((col) => (
                        <span
                            key={col.key}
                            className={`detail-linked-data-grid-th${col.key === primaryKey ? " is-primary" : ""}`}
                            role="columnheader"
                        >
                            {col.label}
                        </span>
                    ))}
                    {interactive ? (
                        <span className="detail-linked-data-grid-th is-action" aria-hidden />
                    ) : null}
                </div>

                <div className="detail-linked-data-grid-body" role="rowgroup">
                    {rows.map((row, rowIndex) => {
                        const rowProps = interactive
                            ? {
                                  className: "detail-linked-data-grid-row is-clickable",
                                  onClick: (e) => {
                                      if (stopRowClickTarget(e)) return;
                                      onRowClick(row);
                                  },
                                  onKeyDown: (e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                          e.preventDefault();
                                          onRowClick(row);
                                      }
                                  },
                                  tabIndex: 0,
                                  role: "button",
                              }
                            : {className: "detail-linked-data-grid-row"};

                        return (
                            <div
                                key={row.id ?? rowIndex}
                                {...rowProps}
                                style={{gridTemplateColumns: gridTemplate}}
                                role="row"
                            >
                                {gridColumns.map((col) => {
                                    const cell = renderLinkedGridCell(row, col, rowIndex);
                                    const isPrimary = col.key === primaryKey;
                                    return (
                                        <div
                                            key={col.key}
                                            className={`detail-linked-data-grid-td${isPrimary ? " is-primary" : ""}`}
                                            role="cell"
                                        >
                                            {cell}
                                        </div>
                                    );
                                })}
                                {interactive ? (
                                    <span className="detail-linked-data-grid-action" aria-hidden>
                                        <ChevronRightIcon className="detail-linked-data-grid-chevron" />
                                    </span>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
